import api from './api';
import { fromJsonApi, fromJsonApiArray } from '@/lib/utils';
import { artifactService, type Artifact } from './artifactService';
import type {
  Project,
  Task,
  SpecDocument,
  Workflow,
  Prototype,
  ApiResponse,
  PaginatedResponse,
  ProjectFormData,
  TaskFormData,
} from '@/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface JsonApiResponse {
  success: boolean;
  data: any;
  meta?: {
    total_count?: number;
    current_page?: number;
    total_pages?: number;
    message?: string;
  };
}

// Helper function to convert Artifact to SpecDocument
function artifactToSpecDocument(artifact: Artifact): SpecDocument {
  return {
    id: artifact.id,
    title: artifact.name,
    slug: artifact.slug || null,
    category: artifact.category || 'other',
    status: artifact.doc_status || 'draft',
    content: artifact.content || null,
    summary: artifact.summary || null,
    tags: artifact.tags || [],
    outline: artifact.outline || null,
    metadata: artifact.metadata || {},
    version: artifact.version || 1,
    checksum: artifact.checksum || null,
    ai_generated: artifact.ai_generated || false,
    ai_model: artifact.ai_model || null,
    ai_context: artifact.ai_context || null,
    created_at: artifact.created_at,
    updated_at: artifact.updated_at,
    // Confirmation fields
    confirmed: artifact.confirmed ?? false,
    confirmed_at: artifact.confirmed_at ?? null,
    confirmed_by_name: artifact.confirmed_by_name ?? null,
    confirmation_note: artifact.confirmation_note ?? null,
    requires_confirmation: artifact.requires_confirmation ?? false,
    // Computed attributes
    word_count: artifact.word_count || null,
    reading_time_minutes: artifact.reading_time_minutes || null,
    content_preview: artifact.content_preview || null,
    created_by_name: artifact.created_by_name || null,
    last_modified_by_name: artifact.last_modified_by_name || null,
    is_published: artifact.is_published || false,
    can_edit: artifact.can_edit ?? true,
    can_approve: (artifact as Artifact & { can_approve?: boolean }).can_approve ?? false,
    has_embeddings: artifact.has_embeddings || false,
    needs_embedding: artifact.needs_embedding || false,
    embedding_count: artifact.embedding_count || 0,
    category_display: artifact.category_display || null,
  };
}

export const projectService = {
  // Projects
  async getProjects(page = 1, pageSize = 20): Promise<PaginatedResponse<Project>> {
    const response = await api.get<JsonApiResponse>('/projects', {
      params: { page, pageSize },
    });
    // Backend returns JSON:API format array in data field
    const projects = fromJsonApiArray<Project>(response.data.data || []);
    return {
      data: projects,
      total: response.data.meta?.total_count || projects.length,
      page: response.data.meta?.current_page || page,
      pageSize,
      totalPages: response.data.meta?.total_pages || 1,
    };
  },

  async getProject(id: string): Promise<Project> {
    const response = await api.get<JsonApiResponse>(`/projects/${id}`);
    return fromJsonApi<Project>(response.data.data);
  },

  async createProject(data: ProjectFormData): Promise<Project> {
    const response = await api.post<JsonApiResponse>('/projects', { project: data });
    return fromJsonApi<Project>(response.data.data);
  },

  async updateProject(id: string, data: Partial<ProjectFormData>): Promise<Project> {
    const response = await api.patch<JsonApiResponse>(`/projects/${id}`, { project: data });
    return fromJsonApi<Project>(response.data.data);
  },

  async deleteProject(id: string): Promise<void> {
    await api.delete(`/projects/${id}`);
  },

  async archiveProject(id: string): Promise<Project> {
    const response = await api.post<ApiResponse<Project>>(`/projects/${id}/archive`);
    return response.data.data;
  },

  async transferOwnership(projectId: string, newOwnerId: string): Promise<Project> {
    const response = await api.post<ApiResponse<Project>>(
      `/projects/${projectId}/transfer_ownership`,
      { new_owner_id: newOwnerId }
    );
    return response.data.data;
  },

  // Project Members
  async getProjectMembers(projectId: string): Promise<{ id: string; name: string; email: string; role: string }[]> {
    const response = await api.get<ApiResponse<{ id: string; name: string; email: string; role: string }[]>>(
      `/projects/${projectId}/memberships`
    );
    return response.data.data;
  },

  // Tasks
  async getTasks(projectId: string): Promise<Task[]> {
    const response = await api.get<ApiResponse<Task[]>>(`/projects/${projectId}/tasks`);
    return response.data.data;
  },

  async getTask(projectId: string, taskId: string): Promise<Task> {
    const response = await api.get<ApiResponse<Task>>(
      `/projects/${projectId}/tasks/${taskId}`
    );
    return response.data.data;
  },

  async createTask(projectId: string, data: TaskFormData): Promise<Task> {
    const response = await api.post<ApiResponse<Task>>(
      `/projects/${projectId}/tasks`,
      data
    );
    return response.data.data;
  },

  async updateTask(
    projectId: string,
    taskId: string,
    data: Partial<TaskFormData>
  ): Promise<Task> {
    const response = await api.patch<ApiResponse<Task>>(
      `/projects/${projectId}/tasks/${taskId}`,
      data
    );
    return response.data.data;
  },

  async deleteTask(projectId: string, taskId: string): Promise<void> {
    await api.delete(`/projects/${projectId}/tasks/${taskId}`);
  },

  async updateTaskStatus(
    projectId: string,
    taskId: string,
    status: Task['status']
  ): Promise<Task> {
    const response = await api.patch<ApiResponse<Task>>(
      `/projects/${projectId}/tasks/${taskId}/status`,
      { status }
    );
    return response.data.data;
  },

  // Spec Documents - 이제 artifactService를 통해 처리
  async getSpecDocument(projectId: string): Promise<SpecDocument | null> {
    try {
      const response = await artifactService.getDocuments(projectId, {}, 1, 1);
      const docs = response.data || [];
      if (docs.length === 0) return null;
      // Artifact를 SpecDocument 형식으로 변환
      const artifact = docs[0];
      return artifactToSpecDocument(artifact);
    } catch {
      return null;
    }
  },

  async updateSpecDocument(projectId: string, content: string, specId?: string): Promise<SpecDocument> {
    if (specId) {
      const artifact = await artifactService.updateArtifact(projectId, specId, { content });
      return artifactToSpecDocument(artifact);
    }
    // 새로 생성 - PRD로 생성 (첫 번째 문서)
    const artifact = await artifactService.createArtifact(projectId, {
      name: 'Main Spec',
      artifact_type: 'document',
      content,
      category: 'prd',
    });
    return artifactToSpecDocument(artifact);
  },

  // Workflows - 목록에서 첫 번째 가져오기
  async getWorkflow(projectId: string): Promise<Workflow | null> {
    const response = await api.get<{ workflows: Workflow[]; pagination: unknown }>(
      `/projects/${projectId}/workflows`
    );
    const workflows = response.data.workflows || [];
    return workflows.length > 0 ? workflows[0] : null;
  },

  async updateWorkflow(projectId: string, workflow: Partial<Workflow>, workflowId?: string): Promise<Workflow> {
    if (workflowId) {
      const response = await api.patch<{ workflow: Workflow }>(
        `/projects/${projectId}/workflows/${workflowId}`,
        { workflow }
      );
      return response.data.workflow;
    }
    // 새로 생성
    const response = await api.post<{ workflow: Workflow }>(
      `/projects/${projectId}/workflows`,
      { workflow: { ...workflow, name: workflow.name || 'Main Workflow' } }
    );
    return response.data.workflow;
  },

  // Prototypes - 목록에서 첫 번째 가져오기
  async getPrototype(projectId: string): Promise<Prototype | null> {
    const response = await api.get<{ status: string; data: Prototype[]; meta: unknown }>(
      `/projects/${projectId}/prototypes`
    );
    const prototypes = response.data.data || [];
    return prototypes.length > 0 ? prototypes[0] : null;
  },

  async updatePrototype(projectId: string, data: Partial<Prototype>, prototypeId?: string): Promise<Prototype> {
    if (prototypeId) {
      const response = await api.patch<{ status: string; data: Prototype; meta: unknown }>(
        `/projects/${projectId}/prototypes/${prototypeId}`,
        { prototype: data }
      );
      return response.data.data;
    }
    // 새로 생성
    const response = await api.post<{ status: string; data: Prototype; meta: unknown }>(
      `/projects/${projectId}/prototypes`,
      { prototype: { ...data, name: data.name || 'Main Prototype' } }
    );
    return response.data.data;
  },
};
